// M08 task-07 (B2): pure request builder for the raw logo fetches in
// Settings.tsx. The multipart PUT /settings/logo isn't a JSON shape the typed
// client surfaces, so the page keeps raw fetch — but the request must target
// the hub API origin (`hubBaseUrl`) and carry cookies (`credentials:
// 'include'`, unconditional — harmless same-origin, required cross-origin;
// mirrors the Login.tsx raw-fetch precedent).
import { createSessionFetch } from '@verybigthings/trackframe-hub-client';

export type LogoVariant = 'light' | 'dark';

// The light logo lives at /settings/logo, the dark-mode variant at
// /settings/logo/dark (HUB-125 D7).
export function logoPath(variant: LogoVariant): string {
  return variant === 'dark' ? '/settings/logo/dark' : '/settings/logo';
}

export function buildLogoRequest(
  hubBaseUrl: string,
  method: 'PUT' | 'DELETE',
  body?: FormData,
  variant: LogoVariant = 'light',
): { url: string; init: RequestInit } {
  return {
    url: `${hubBaseUrl}${logoPath(variant)}`,
    init: { method, credentials: 'include', ...(body ? { body } : {}) },
  };
}

/**
 * Sends the logo PUT/DELETE through the client's silent session refresh
 * (HUB-104), exactly like every typed `HubClient` call on the page. A bare
 * `fetch` here surfaced the hub's 401 "Missing credential" the moment the
 * `tf_sso` cookie expired while `tf_refresh` was still valid — pinned by
 * logo-request.test.ts.
 */
export function createLogoSender(
  hubBaseUrl: string,
  variant: LogoVariant = 'light',
): (method: 'PUT' | 'DELETE', body?: FormData) => Promise<Response> {
  const sessionFetch = createSessionFetch({ hubBaseUrl });
  return (method, body) => {
    const { url, init } = buildLogoRequest(hubBaseUrl, method, body, variant);
    return sessionFetch(url, init);
  };
}
